import React, { useState } from "react";
import AddNewMedication from "../addMedication";
import { TableColumnsItem } from "../../../../interfaces/components/mainTable";
import { ButtonType, ColumnType, ModalType } from "../../../../shared/utils/enums";
import MainTable from "../../../../components/mainTable";
import { useDispatch } from "react-redux";
import { showModal } from "../../../../connected-components/modal/reducer";
import { screens } from "../../../../config/routes";
import { useNavigate } from "react-router-dom";
import { fetchMedications, deleteMedication } from "./actions";
import { Medication } from "../../../../interfaces/api/medications";
import { Trash2 } from "lucide-react";

const MedicationsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [medications, setMedications] = useState<Medication[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    const loadMedications = async () => {
      setIsLoading(true);
      const res = await fetchMedications(1);
      if (res && res.result === 'success' && res.content && res.content.data) {
        setMedications(res.content.data);
      }
      setIsLoading(false);
    };
    loadMedications();
    // eslint-disable-next-line
  }, [currentPage, pageSize]);

  const handleRowPress = (medication: Medication) => {
    navigate(screens.medicationDetails?.replace(':id', String(medication.id)));
  };

  const handlePageChange = ({
    pageIndex,
    pageSize: newPageSize,
  }: {
    pageIndex: number;
    pageSize: number;
  }) => {
    setCurrentPage(pageIndex);
    setPageSize(newPageSize);
  };

  const handleDeleteMedication = async (medication: Medication) => {
    const res = await deleteMedication(medication.id);
    if (res && res.result === 'success') {
      setMedications(prev => prev.filter(m => m.id !== medication.id));
    }
  };

  const handleAddNewMedication = () => {
    dispatch(showModal({
      type: ModalType.COMPONENT,
      title: 'Add New Medication',
      component: <AddNewMedication onSuccess={(newMedication) => {
        setMedications(prev => [newMedication, ...prev]);
      }} />,
    }));
  };

  const handleDeleteClick = (medication: Medication) => {
    dispatch(showModal({
      type: ModalType.QUESTION,
      title: 'Delete Medication',
      message: `Are you sure you want to delete ${medication.name}?`,
      onConfirm: () => handleDeleteMedication(medication),
    }));
  };

  const getActionButtonClasses = (type: string) => {
    const baseClasses = "p-2 rounded-full transition-colors duration-200";
    switch (type) {
      case "delete":
        return `${baseClasses} text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10`;
      default:
        return `${baseClasses} text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-500/10`;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const medicationTableColumns: TableColumnsItem[] = [
    {
      key: "name",
      name: "Medication Name",
      type: ColumnType.TEXT,
      width: "200px",
      cardTitle: true,
      customCellClass: "font-medium text-gray-900 dark:text-gray-100",
    },
    {
      key: "dosage",
      name: "Dosage",
      type: ColumnType.TEXT,
      width: "120px",
      cardOrder: 2,
      customCellClass: "text-gray-600 dark:text-gray-400",
    },
    {
      key: "frequency",
      name: "Frequency",
      type: ColumnType.TEXT,
      width: "120px",
      cardOrder: 3,
      customCellClass: "text-gray-600 dark:text-gray-400",
    },
    {
      key: "start_date",
      name: "Start Date",
      type: ColumnType.CUSTOM,
      width: "120px",
      cardOrder: 4,
      customValue: (row: Medication) => formatDate(row.start_date),
      customCellClass: "text-gray-600 dark:text-gray-400",
    },
    {
      key: "end_date",
      name: "End Date",
      type: ColumnType.CUSTOM,
      width: "120px",
      cardOrder: 5,
      customValue: (row: Medication) => formatDate(row.end_date),
      customCellClass: "text-gray-600 dark:text-gray-400",
    },
    {
      key: "route",
      name: "Route",
      type: ColumnType.TEXT,
      width: "120px",
      cardOrder: 6,
      customCellClass: "text-gray-600 dark:text-gray-400",
    },
    {
      key: "is_active",
      name: "Status",
      type: ColumnType.CUSTOM,
      width: "100px",
      cardOrder: 7,
      customValue: (row: Medication) => row.is_active ? "Active" : "Inactive",
      customCellClass: "text-gray-600 dark:text-gray-400",
    },
    {
      key: "actions",
      name: "Actions",
      type: ColumnType.BUTTON,
      width: "100px",
      cardOrder: 999,
      buttonsList: [
        {
          type: ButtonType.ICON,
          icon: () => <Trash2 className="w-4 h-4" />,
          onPress: (e: React.MouseEvent, row: Medication) => {
            e.stopPropagation();
            handleDeleteClick(row);
          },
          cssClass: getActionButtonClasses("delete"),
        },
      ],
    },
  ];

  return (
    <div className="py-4">
      <MainTable
        columns={medicationTableColumns}
        data={medications}
        isLoading={isLoading}
        tableClass="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
        onRowPress={handleRowPress}
        showPagination={true}
        totalData={medications.length}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChanged={handlePageChange}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        showSearch={true}
        addButtonLabel="Add New Medication"
        onAddNewItemPress={handleAddNewMedication}
      />
    </div>
  );
};

export default MedicationsList;